import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import DictionaryFormFields from "@/components/dictionaries/DictionaryFormFields";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import PageContent from "@/components/PageContent";
import { useToast } from "@/components/ui/use-toast";
import { getDictionaryConfig } from "@/containers/dictionaries/getDictionaryConfig";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { ApiError } from "@/services/api";
import { ReadonlyObject } from "@/types/readOnly";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SaveButton from "@/components/SaveButton";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useOfflineAwareMutation } from "@/lib/hooks/useOfflineAwareMutation";
import { useCachedMutation } from "@/hooks/useCachedMutation";

export default function DictionaryCreator<
  TRead extends ReadonlyObject,
  TWrite extends object,
>({ dict }: DictionaryContext) {
  const id = "new";
  const { t } = useTranslation();
  const config = getDictionaryConfig(dict);
  const labels = config.getLabels(t);
  const Icon = config.Icon;
  const { toast } = useToast();
  const { can_create } = useModulePermissions();
  const handleOfflineAwareMutationSuccess = useOfflineAwareMutation<TRead>(
    config.id,
  );
  const mutationFn: MutationFunction<TRead, TWrite> = async (values) =>
    config.service.post<TWrite, TRead>(values);

  const mutation = useCachedMutation<TRead, ApiError, TWrite>({
    mutationFn,
    onSuccess: (response) => {
      handleOfflineAwareMutationSuccess({
        type: "create",
        response,
        path: config.path,
        successMessage: labels.success.create,
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<TWrite>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues || {}) as DefaultValues<TWrite>,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <DictionaryForm>
        <PageContent
          icon={<Icon className="w-6 h-6 mr-3" />}
          title={labels.title}
          footer={<SaveButton />}
        >
          <DictionaryFormFields />
        </PageContent>
      </DictionaryForm>
    </DictionaryRecordContextProvider>
  );
}
